import type { Theme } from '@mui/material';
import { DEFAULT_NUMERIC_RANGE } from '../constants';
import { QuestionType, type CampaignQuestion, type NumericQuestion } from '../types';

/**
 * Determines the display title for a question based on its type and content.
 *
 * @param {CampaignQuestion} question - Question
 * @param {number} index - Question index.
 * @returns {string} Formatted title to display
 */
export const getQuestionDisplayTitle = (question: CampaignQuestion, index: number): string => {
    const isFormula = question.type === QuestionType.FORMULA;

    if (isFormula) {
        // Before title field has been added, formulas used question text as a title.
        return question.title ? `${index}. ${question.title}` : `${index}. ${question.text}`;
    }

    return `${index}. ${question.title ? question.title : 'Question'}`;
};

/**
 * Transforms a question to a new type, preserving common fields and adding type-specific defaults.
 *
 * @param {CampaignQuestion} question - The question to transform
 * @param {QuestionType} newType - The new type to transform to
 * @returns {CampaignQuestion} The transformed question
 */
export const transformQuestionType = (question: CampaignQuestion, newType: QuestionType): CampaignQuestion => {
    const baseQuestion = {
        id: question.id,
        text: question.text,
        title: question.title,
    };

    switch (newType) {
        case QuestionType.TEXT:
            return {
                ...baseQuestion,
                type: QuestionType.TEXT,
            } as CampaignQuestion;
        case QuestionType.BOOLEAN:
            return {
                ...baseQuestion,
                type: QuestionType.BOOLEAN,
            } as CampaignQuestion;
        case QuestionType.NUMERIC:
            return {
                ...baseQuestion,
                type: QuestionType.NUMERIC,
                minValue: DEFAULT_NUMERIC_RANGE.MIN,
                maxValue: DEFAULT_NUMERIC_RANGE.MAX,
                useRatingScale: false,
            } as CampaignQuestion;
        case QuestionType.MULTISELECT:
            return {
                ...baseQuestion,
                type: QuestionType.MULTISELECT,
                options: [],
            } as CampaignQuestion;
        case QuestionType.FORMULA:
            return {
                ...baseQuestion,
                type: QuestionType.FORMULA,
                formula: '',
            } as CampaignQuestion;
        default:
            return question;
    }
};

/**
 * Generates Paper component styles for question edit items.
 * Handles highlighting styling.
 *
 * @param {Theme} theme - MUI theme object
 * @param {boolean} isHighlighted - Whether the question is highlighted
 * @returns {object} MUI sx prop object
 */
export const getQuestionEditPaperStyles = (theme: Theme, isHighlighted?: boolean) => ({
    p: 1.5,
    pt: 1,
    backgroundColor: isHighlighted ? theme.palette.success.light : theme.palette.gray[10],
    transition: 'background-color 0.6s ease, box-shadow 0.2s ease, border-color 0.6s ease',
    ...(isHighlighted && {
        borderColor: theme.palette.success.main,
        boxShadow: '0 0 0 2px rgba(76, 175, 80, 0.2)',
    }),
});

/**
 * Checks if a question has content based on its type.
 *
 * @param {CampaignQuestion} question - Question
 * @returns {boolean} True if question has content, false otherwise
 */
export const shouldConfirmDeleteQuestion = (question: CampaignQuestion): boolean => {
    const title = question.title?.trim();
    const body = question.type === QuestionType.FORMULA ? question.formula : question.text;

    return Boolean(title || body?.trim());
};

/**
 * Checks if a question is a numeric question.
 *
 * @param {CampaignQuestion} question - Question
 * @returns {boolean}
 */
export const isNumericQuestion = (question: CampaignQuestion): question is NumericQuestion => question.type === QuestionType.NUMERIC;

/**
 * Checks if a formula question is configured as a quality score.
 * A formula is considered a quality score if it has isQualityScore set to true.
 *
 * @param {CampaignQuestion} question - Question
 * @returns {boolean}
 */
export const isQualityScoreFormula = (question: CampaignQuestion): boolean => {
    return question.type === QuestionType.FORMULA && !!question.isQualityScore;
};

/**
 * Returns whether a numeric question has rating scale enabled.
 *
 * @param {CampaignQuestion} question - Question
 * @returns {boolean}
 */
export const hasRatingScale = (question: CampaignQuestion): boolean => {
    if (question.type !== QuestionType.NUMERIC) return false;

    return Boolean(question.useRatingScale);
};
